import { FC, ReactNode, useEffect } from "react";

import {
  useHookForm,
  useMerchantApiMutation,
  useNotifications,
} from "../../../../hooks";
import {
  IRefundPaymentIssueToCustomer,
  IHookForm,
  IViewVirtualAccountTransferRefund,
} from "../../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../../utils";

import { View } from "./View";
import { refundPaymentIssueToCustomerSchema } from "./validationSchema";

export interface IRefundToCustomerFormViewProps
  extends IHookForm<IRefundPaymentIssueToCustomer> {
  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: ReactNode | null;
  refundAmount: number;
  refundCurrency: string;
}

export interface IRefundToCustomerFormProps {
  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: ReactNode | null;
  // refund details
  refundAmount: number;
  refundCurrency: string;
  // form valuess
  transferRefundId: string;
}

export const RefundToCustomerForm: FC<IRefundToCustomerFormProps> = (props) => {
  const notificationsHandler = useNotifications();

  const {
    isLoading: isLoadingAcceptAsPartial,
    isSuccess: isSuccessAcceptAsPartial,
    // isError: isErrorAcceptAsPartial,
    data: acceptAsPartialApiResponse,
    mutate: acceptAsPartialPayment,
  } = useMerchantApiMutation<
    IRefundPaymentIssueToCustomer,
    IViewVirtualAccountTransferRefund
  >(
    "refundPaymentIssueToCustomer",
    ["getVirtualAccountTransferRefundById"],
    {
      // ignoreActivityBlocker: true,
    },
    (urlTemplate) => urlTemplate.replace("id", props.transferRefundId)
  ).usePostMutation;

  const initialFormValues: IRefundPaymentIssueToCustomer = {
    transfer_refund_id: props.transferRefundId,
    account_number: "",
    bank_code: "",
    description: "",
  };

  const handleApiSubmit = (data: IRefundPaymentIssueToCustomer) => {
    acceptAsPartialPayment(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset } = useHookForm({
    initialFormValues,
    validationSchema: refundPaymentIssueToCustomerSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  errors && console.warn("RefundToCustomerForm.errors: ", errors);

  useEffect(() => {
    reset(initialFormValues);
  }, [props.transferRefundId]);

  useEffect(() => {
    if (isSuccessAcceptAsPartial) {
      reset();

      // display a notification of success
      notificationsHandler.showNotification({
        ariaLabel: "info notification",
        title: "Payment Issue Settled Successfully",
        kind: "info",
        message:
          "The payment issue has been resolved. It has been refunded to the customer.",
        statusIconDescription: "notification",
        timeout: 10000,
      });
    }
  }, [isSuccessAcceptAsPartial]);

  return (
    <View
      {...props}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
